import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Maximize2, MessageSquare, Frame, Receipt } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const SHOTS = [
  "https://images.unsplash.com/photo-1680624629496-0a2c2444153e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHw0fHxkYXJrJTIwbW9vZHklMjB3ZWRkaW5nJTIwcGhvdG9ncmFwaHl8ZW58MHx8fHwxNzg3NTQ5ODA1fDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1776262145093-ab3f69760686?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBlZGl0b3JpYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODc1NDk4MDV8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1519741196428-6a2175fa2557?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxkYXJrJTIwbW9vZHklMjB3ZWRkaW5nJTIwcGhvdG9ncmFwaHl8ZW58MHx8fHwxNzg3NTQ5ODA1fDA&ixlib=rb-4.1.0&q=85",
  "https://images.pexels.com/photos/35733628/pexels-photo-35733628.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.unsplash.com/photo-1762843353166-e0542bba1a66?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBlZGl0b3JpYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODc1NDk4MDV8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1762504013915-c1faf57f291b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxtb29keSUyMGJyaWRhbCUyMHBvcnRyYWl0JTIwZGFya3xlbnwwfHx8fDE3ODc4MzI3NzZ8MA&ixlib=rb-4.1.0&q=85",
];

const SCRIPT = [0, 3, 1, 4];
const QUOTA = 4;

/** Auto-playing replica of the real grid-and-checkmark selection experience. */
export const GridSelectionDemo = () => {
  const { t } = useI18n();
  const [picked, setPicked] = useState([]);
  const [manual, setManual] = useState(false);

  useEffect(() => {
    if (manual) return;
    let step = 0;
    const tick = setInterval(() => {
      step += 1;
      if (step > SCRIPT.length) {
        setPicked([]);
        step = 0;
        return;
      }
      setPicked(SCRIPT.slice(0, step));
    }, 1300);
    return () => clearInterval(tick);
  }, [manual]);

  const toggle = (i) => {
    setManual(true);
    setPicked((p) => (p.includes(i) ? p.filter((x) => x !== i) : p.length >= QUOTA ? p : [...p, i]));
  };

  return (
    <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20" data-testid="grid-selection-demo">
      <div className="w-full lg:w-[52%] shrink-0">
        <div className="border border-[#2A2A2A] bg-[#0B0B0B] p-3 sm:p-4 shadow-[0_0_90px_rgba(212,175,55,0.07)]">
          <div className="flex items-center justify-between pb-3 border-b border-[#1E1E1E]">
            <span className="font-mono-accent text-[9px] tracking-[0.22em] uppercase text-[#D4AF37]">
              01 {t("step_select")}
            </span>
            <span className="font-mono-accent text-[9px] tracking-[0.16em] uppercase text-[#A8A8A8]"
              data-testid="demo-counter">
              {picked.length}/{QUOTA} {t("edits")}
            </span>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 sm:gap-3">
            {SHOTS.map((src, i) => {
              const on = picked.includes(i);
              return (
                <button key={src} data-testid={`demo-tile-${i}`} onClick={() => toggle(i)}
                  className="relative block overflow-hidden group"
                  aria-pressed={on} aria-label={`Photo ${i + 1}`}>
                  <img src={src} alt="" loading="lazy"
                    className={`w-full h-24 sm:h-32 object-cover transition-all duration-500 ${
                      on ? "opacity-100" : "opacity-60 group-hover:opacity-90"
                    }`} />
                  <span className={`absolute inset-0 border-2 transition-colors duration-300 ${
                    on ? "border-[#D4AF37]" : "border-transparent"
                  }`} />
                  <motion.span
                    initial={false}
                    animate={{ scale: on ? 1 : 0.6, opacity: on ? 1 : 0 }}
                    transition={{ type: "spring", stiffness: 420, damping: 22 }}
                    className="absolute top-1.5 left-1.5 w-6 h-6 bg-[#D4AF37] text-black flex items-center justify-center"
                  >
                    <Check size={13} strokeWidth={3} />
                  </motion.span>
                  <span className="absolute bottom-1.5 right-1.5 glass border border-[#2A2A2A] p-1 text-[#F3F3F1] opacity-0 group-hover:opacity-100 transition-opacity">
                    <Maximize2 size={10} />
                  </span>
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex items-center gap-3 overflow-x-auto pt-3 border-t border-[#1E1E1E]">
            {["step_select", "step_review", "step_extra", "step_prints", "step_confirm"].map((s, i) => (
              <span key={s} className={`shrink-0 font-mono-accent text-[9px] tracking-[0.16em] uppercase ${
                i === 0 ? "text-[#D4AF37]" : "text-[#3d3d3d]"
              }`}>
                0{i + 1} {t(s)}
              </span>
            ))}
          </div>
        </div>
        <p className="mt-4 text-center text-[10px] tracking-[0.18em] uppercase text-[#707070]">{t("demo_try")}</p>
      </div>

      <div className="flex-1">
        <p className="eyebrow mb-5">{t("demo_eyebrow")}</p>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium leading-[1.08]">
          {t("demo_title")}
        </h2>
        <p className="mt-6 text-sm sm:text-base text-[#A8A8A8] max-w-lg leading-relaxed">{t("demo_body")}</p>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#2A2A2A] border border-[#2A2A2A]">
          {[
            { icon: MessageSquare, label: t("demo_f1") },
            { icon: Frame, label: t("demo_f2") },
            { icon: Receipt, label: t("demo_f3") },
          ].map((f) => (
            <div key={f.label} className="bg-[#0B0B0B] p-6">
              <f.icon size={18} strokeWidth={1.3} className="text-[#D4AF37]" />
              <p className="mt-4 text-sm text-[#F3F3F1] leading-relaxed">{f.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
